import axios from "axios";
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  AxiosProgressEvent,
} from "axios";
import { getApiConfig } from "@config/api.config";
import { STORAGE_KEYS } from "@config/app.config";
import { getLocalStorage, setLocalStorage, removeLocalStorage } from "@common";

const AUTH_TOKEN_KEY = "chat_auth_token";
const AUTH_USER_KEY = "chat_auth_user";

class ApiClient {
  private instance: AxiosInstance;
  private token: string | null = null;

  constructor() {
    const config = getApiConfig();

    this.instance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout,
      headers: {
        "Content-Type": "application/json",
        ...config.headers,
      },
    });

    const savedToken = getLocalStorage(STORAGE_KEYS.AUTH_TOKEN);
    if (savedToken) {
      this.token = savedToken;
    }

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.instance.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        } else {
          const savedToken = getLocalStorage(STORAGE_KEYS.AUTH_TOKEN);
          if (savedToken) {
            this.token = savedToken;
            config.headers.Authorization = `Bearer ${savedToken}`;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.instance.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          this.handleUnauthorized();
        }

        if (error.response?.status === 403) {
          console.error("Access forbidden:", error.response.data);
        }

        if (error.response?.status === 500) {
          console.error("Server error:", error.response.data);
        }

        return Promise.reject(error);
      }
    );
  }

  setToken(token: string | null): void {
    this.token = token;
    if (token) {
      setLocalStorage(STORAGE_KEYS.AUTH_TOKEN, token);
    } else {
      removeLocalStorage(STORAGE_KEYS.AUTH_TOKEN);
    }
  }

  clearToken(): void {
    this.token = null;
    removeLocalStorage(STORAGE_KEYS.AUTH_TOKEN);
  }

  private handleUnauthorized(): void {
    const oldToken = getLocalStorage(AUTH_TOKEN_KEY);
    this.clearToken();
    removeLocalStorage(AUTH_TOKEN_KEY);
    removeLocalStorage(AUTH_USER_KEY);
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new StorageEvent("storage", {
          key: AUTH_TOKEN_KEY,
          oldValue: oldToken,
          newValue: null,
          storageArea: localStorage,
        })
      );
      window.dispatchEvent(
        new CustomEvent("auth:logout", { detail: { reason: "unauthorized" } })
      );
      const isStandalone = !(window as Window & { __CHAT_EMBEDDED_MODE__?: boolean })
        .__CHAT_EMBEDDED_MODE__;
      if (isStandalone) {
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    }
  }

  getToken(): string | null {
    return this.token;
  }

  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.get(url, config);
    return response.data;
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.post(
      url,
      data,
      config
    );
    return response.data;
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.put(
      url,
      data,
      config
    );
    return response.data;
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.patch(
      url,
      data,
      config
    );
    return response.data;
  }

  async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.delete(url, config);
    return response.data;
  }

  async upload<T = unknown>(
    url: string,
    file: File | FormData,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<T> {
    const formData = file instanceof FormData ? file : new FormData();
    if (file instanceof File) {
      formData.append("file", file);
    }

    const response: AxiosResponse<T> = await this.instance.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });

    return response.data;
  }

  async download(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.instance.get(url, {
      ...config,
      responseType: "blob",
    });
    return response.data;
  }

  getInstance(): AxiosInstance {
    return this.instance;
  }
}

export const apiClient = new ApiClient();

export { ApiClient };
export default ApiClient;
